/**
 * Frota · Manutenção · Consulta por placa — reproduz `docs/manutencao/manutenção
 * preventiva ou corretiva.png`: busca, cartão do veículo e as duas escolhas
 * (preventiva com os itens já selecionados, corretiva com exemplos), mais as
 * alertas do veículo.
 *
 * A preventiva abre a O.S. com TODOS os itens elegíveis selecionados; a revisão
 * (desmarcar) acontece dentro da O.S. A corretiva leva aos cartões ilustrados.
 */
import { BackButton } from "../components/BackButton";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenancePlanPlateDto } from "@bi/types";
import { api, ApiError } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { fmtDay } from "../features/maintenance/preventive-ui";
import { groupByPlate, plateSummary, preventiveIconSrc, suggestedForOs } from "../features/maintenance/preventive-plate-ui";
import { IconAlertTriangle, IconTruck, IconX } from "../components/icons";

const EXEMPLOS: Array<{ t: string; icone: string }> = [
  { t: "Falhas no motor ou perda de desempenho", icone: "⚠" },
  { t: "Ruídos e vibrações anormais", icone: "⚙" },
  { t: "Problemas nos freios, suspensão ou direção", icone: "◎" },
  { t: "Falhas elétricas ou eletrônicas", icone: "ϟ" },
  { t: "Vazamentos, superaquecimento ou consumo anormal", icone: "🌡" },
  { t: "Outros serviços não previstos no plano preventivo", icone: "🔧" },
];

export function MaintenancePlatePage() {
  const { plate = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useCapabilities();
  const [erro, setErro] = useState<string | null>(null);
  const placa = plate.toUpperCase();
  const [busca, setBusca] = useState(placa);

  const plano = useQuery({
    queryKey: ["maintenance", "preventive", "plate", placa],
    queryFn: () => api.get<MaintenancePlanPlateDto>(`/maintenance/preventive/plates/${encodeURIComponent(placa)}`),
  });

  const rows = plano.data?.rows ?? [];
  const g = groupByPlate(rows)[0] ?? null;
  const resumo = plateSummary(rows);
  const sugeridos = suggestedForOs(rows);
  const osAberta = rows.find((r) => r.openWorkOrder)?.openWorkOrder ?? null;
  const ultima = rows
    .filter((r) => r.lastExecution)
    .sort((a, b) => new Date(b.lastExecution!.executedAt).getTime() - new Date(a.lastExecution!.executedAt).getTime())[0]?.lastExecution ?? null;
  const proxima = [...rows]
    .filter((r) => r.status.nextKm != null || r.status.nextDate)
    .sort((a, b) => (a.status.kmRemaining ?? a.status.daysRemaining ?? Infinity) - (b.status.kmRemaining ?? b.status.daysRemaining ?? Infinity))[0];
  const alertas = rows
    .filter((r) => r.status.status === "vencido" || r.status.status === "vencendo" || r.status.kmStale)
    .slice(0, 3);

  const abrir = useMutation({
    mutationFn: () => api.post<{ id: string }>("/maintenance/preventive/work-orders", { plate: placa, componentCodes: sugeridos.map((r) => r.componentCode) }),
    onSuccess: (o) => { qc.invalidateQueries({ queryKey: ["maintenance"] }); navigate(`/frota/manutencao/ordens/${o.id}`); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível abrir a preventiva."),
  });

  const buscar = () => {
    const p = busca.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (p.length >= 5) navigate(`/frota/manutencao/placa/${p}`);
  };
  const podeAbrir = can("workorder.manage");
  const prox = (r: (typeof rows)[number]) =>
    [r.status.nextKm != null ? `${fmtNumber(r.status.nextKm)} km` : null, r.status.nextDate ? fmtDay(r.status.nextDate) : null].filter(Boolean).join(" ou ") || "Sem base";

  return (
    <div className="space-y-4">
      <BackButton fallback="/frota/manutencao" />
      <header className="[[data-layout=mobile]_&]:hidden">
        <h2 className="text-4xl font-bold tracking-tight text-gray-900">Central de Manutenção</h2>
        <p className="mt-1 text-lg text-gray-500">Escolha o tipo de atendimento para a placa selecionada.</p>
      </header>

      {/* Consultar veículo por placa */}
      <section className="card flex flex-col gap-3 border-info-100 bg-info-50/60 p-4 lg:flex-row lg:items-center">
        <div className="flex items-center gap-4 lg:w-[22.5rem]">
          <IconTruck className="h-10 w-10 shrink-0 text-gray-800 sm:h-12 sm:w-12" />
          <div>
            <p className="text-lg font-semibold text-gray-900">Consultar veículo por placa</p>
            <p className="text-sm text-gray-500">Digite a placa para ver as opções de manutenção</p>
          </div>
        </div>
        <form className="flex flex-1 items-center gap-3" onSubmit={(e) => { e.preventDefault(); buscar(); }}>
          <span className="flex min-h-[3.25rem] min-w-0 flex-1 items-center gap-2 rounded-xl border border-info-500 bg-white px-4">
            <span className="text-gray-500" aria-hidden="true">⌕</span>
            <input
              className="min-w-0 flex-1 bg-transparent font-mono text-lg font-semibold uppercase outline-none"
              value={busca} onChange={(e) => setBusca(e.target.value.toUpperCase())} aria-label="Placa"
            />
            {busca && <button type="button" onClick={() => setBusca("")} aria-label="Limpar"><IconX className="h-5 w-5 text-gray-500" /></button>}
          </span>
          <button type="submit" className="btn-primary min-h-[3.25rem] px-5 text-lg sm:px-8">Buscar</button>
        </form>
      </section>

      {/* Cartão do veículo */}
      <section className="card grid gap-5 p-5 lg:grid-cols-[1.5fr_1fr_1fr_1fr] lg:divide-x lg:divide-surface-border">
        <div className="flex items-center gap-3 sm:gap-5">
          <img src="/manutencao-bi/caminhao.png" alt="" className="h-[4.75rem] w-[6.5rem] flex-shrink-0 object-contain mix-blend-multiply sm:h-[7.5rem] sm:w-[10.625rem]" />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase text-gray-500">Placa</p>
            <p className="flex flex-wrap items-center gap-2">
              <span className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{placa}</span>
              <span className="rounded-md bg-tm-50 px-2 py-0.5 text-xs font-semibold text-tm-700">Ativo</span>
            </p>
            <p className="text-lg font-semibold text-gray-900">{g?.vehicleModel ?? (plano.isLoading ? "Carregando…" : "Veículo")}</p>
            <p className="text-sm text-gray-500">
              {g?.isTrailer ? "Carreta" : "Cavalo mecânico"}{g?.group ? ` | Grupo: ${g.group.name}` : ""}
            </p>
          </div>
        </div>
        <div className="lg:pl-5">
          <p className="text-xs font-semibold uppercase text-gray-500">KM atual</p>
          {g?.currentKm ? (
            <>
              <p className="text-3xl font-bold text-gray-900">{fmtNumber(g.currentKm.km)} km</p>
              <p className={`text-xs ${g.kmStale ? "text-warning-700" : "text-gray-500"}`}>
                {g.kmStale ? "KM possivelmente desatualizada · " : ""}Atualizado em {g.currentKm.readAt ? fmtDay(g.currentKm.readAt) : "—"} · {g.currentKm.sourceLabel}
              </p>
            </>
          ) : <p className="text-3xl font-bold text-gray-400">{g?.isTrailer ? "Carreta" : "Sem KM"}</p>}
        </div>
        <div className="lg:pl-5">
          <p className="text-xs font-semibold uppercase text-gray-500">Última manutenção</p>
          {ultima ? (
            <>
              <p className="text-2xl font-bold text-gray-900">{fmtDay(ultima.executedAt)}</p>
              <p className="text-sm text-gray-600">{ultima.odometerKm != null ? `${fmtNumber(ultima.odometerKm)} km` : "—"}</p>
              <span className="mt-1 inline-block rounded-md bg-tm-50 px-2 py-0.5 text-xs font-medium text-tm-700">Preventiva</span>
            </>
          ) : <p className="text-2xl font-bold text-gray-400">Sem registro</p>}
        </div>
        <div className="lg:pl-5">
          <p className="text-xs font-semibold uppercase text-gray-500">Próxima preventiva</p>
          {proxima ? (
            <>
              <p className="text-3xl font-bold text-gray-900">{proxima.status.nextKm != null ? `${fmtNumber(proxima.status.nextKm)} km` : fmtDay(proxima.status.nextDate)}</p>
              {proxima.status.nextKm != null && proxima.status.nextDate && <p className="text-sm text-gray-600">ou {fmtDay(proxima.status.nextDate)}</p>}
              <p className="text-xs text-gray-500">
                {proxima.status.kmRemaining != null ? `Em ${fmtNumber(Math.max(0, proxima.status.kmRemaining))} km` : proxima.status.daysRemaining != null ? `Em ${Math.max(0, proxima.status.daysRemaining)} dias` : ""}
              </p>
            </>
          ) : <p className="text-2xl font-bold text-gray-400">—</p>}
        </div>
      </section>

      {erro && <div className="card border-danger-600/30 bg-danger-50 p-3 text-sm text-danger-600">{erro}</div>}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Preventiva */}
        <section className="card flex flex-col gap-4 border-tm-600/25 bg-tm-50/30 p-5">
          <div className="flex items-start gap-4">
            <img src="/manutencao-bi/prev-grande.png" alt="" className="h-[6rem] w-[6.5rem] flex-shrink-0 rounded-2xl object-contain" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-2xl font-semibold text-gray-900">Manutenção preventiva</h3>
                <span className="rounded-md border border-tm-600/30 bg-tm-50 px-2.5 py-1 text-xs font-semibold text-tm-700">★ Recomendado</span>
              </div>
              <p className="mt-1 text-gray-600">Siga o plano de manutenção do fabricante e da Transmasi.</p>
              <p className="mt-1 text-sm text-gray-500">O sistema já selecionou os itens conforme o plano aplicável para este veículo, com base na quilometragem e tempo.</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between border-b border-surface-border pb-2">
              <p className="font-semibold text-gray-900">Itens sugeridos para esta manutenção</p>
              <p className="text-sm text-gray-500">Próxima troca</p>
            </div>
            {plano.isLoading ? (
              <p className="py-4 text-sm text-gray-500">Carregando…</p>
            ) : sugeridos.length ? (
              <ul className="divide-y divide-surface-border">
                {sugeridos.map((r) => {
                  const src = preventiveIconSrc(r.label, "lista");
                  return (
                    <li key={r.id} className="flex items-center gap-3 py-2.5">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-tm-600 text-sm text-white" aria-hidden="true">✓</span>
                      {src ? <img src={src} alt="" className="h-9 w-9 shrink-0 object-contain mix-blend-multiply" /> : <span className="h-9 w-9 shrink-0" />}
                      <span className="min-w-0 flex-1 text-gray-900">{r.label}</span>
                      <span className="text-sm text-gray-600">{prox(r)}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="rounded-lg bg-surface-muted p-3 text-sm text-gray-600">
                {rows.length ? "Todos os itens desta placa já estão em uma O.S. ativa." : "Esta placa ainda não tem plano preventivo. Inclua a placa em um grupo com plano."}
              </p>
            )}
          </div>

          <p className="flex items-center gap-2 rounded-lg bg-tm-50 px-3 py-2 text-sm text-gray-600">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-tm-600 text-xs text-white" aria-hidden="true">i</span>
            Itens, prazos e quilometragem podem variar conforme o plano do veículo.
          </p>

          {osAberta ? (
            <Link className="btn-primary min-h-[3.5rem] justify-center text-lg" to={`/frota/manutencao/ordens/${osAberta.id}`}>
              Já existe a O.S. #{osAberta.number}. Abrir →
            </Link>
          ) : (
            <button className="btn-primary min-h-[3.5rem] justify-center text-lg" disabled={!podeAbrir || abrir.isPending || sugeridos.length === 0} onClick={() => abrir.mutate()}>
              {abrir.isPending ? "Abrindo…" : "🔧  Abrir preventiva  →"}
            </button>
          )}
        </section>

        {/* Corretiva */}
        <section className="card flex flex-col gap-4 border-info-100 bg-info-50/30 p-5">
          <div className="flex items-start gap-4">
            <img src="/manutencao-bi/corr-grande.png" alt="" className="h-[6rem] w-[6.25rem] flex-shrink-0 rounded-2xl object-contain" />
            <div>
              <h3 className="text-2xl font-semibold text-gray-900">Manutenção corretiva</h3>
              <p className="mt-1 text-gray-600">Registre um problema, defeito ou necessidade não planejada.</p>
              <p className="mt-1 text-sm text-gray-500">Abra uma ordem de serviço manualmente, selecione os serviços, registre observações e acompanhe a execução.</p>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <p className="font-semibold text-gray-900">Exemplos de situações</p>
              <span className="h-px flex-1 bg-surface-border" />
            </div>
            <ul className="mt-2 space-y-2.5 text-sm text-gray-700">
              {EXEMPLOS.map((e) => (
                <li key={e.t} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center text-gray-700" aria-hidden="true">{e.icone}</span>{e.t}
                </li>
              ))}
            </ul>
          </div>
          <Link
            className="mt-auto inline-flex min-h-[3.5rem] items-center justify-center gap-2 rounded-lg bg-info-600 px-4 text-lg font-semibold text-white hover:bg-info-700"
            to={`/frota/manutencao/registrar?placa=${placa}`}
          >
            🔧  Abrir corretiva  →
          </Link>
        </section>
      </div>

      {/* Alertas do veículo */}
      <section className="card flex flex-col gap-3 border-warning-100 bg-warning-50/50 p-4 lg:flex-row lg:items-center">
        <div className="flex items-center gap-4 lg:w-[18.75rem]">
          <IconAlertTriangle className="h-10 w-10 text-warning-600" />
          <div>
            <p className="text-lg font-semibold text-gray-900">Alertas do veículo</p>
            <p className="text-sm text-gray-500">Fique atento aos pontos que precisam de atenção.</p>
          </div>
        </div>
        <ul className="grid flex-1 gap-3 sm:grid-cols-3">
          {alertas.map((r) => (
            <li key={r.id} className="flex gap-2 lg:border-l lg:border-warning-100 lg:pl-4">
              <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${r.status.status === "vencido" ? "bg-danger-600" : "bg-warning-600"}`} />
              <span className="text-sm">
                <span className="block font-semibold text-gray-900">{r.status.status === "vencido" ? `${r.label} vencida` : r.label}</span>
                <span className="block text-gray-600">{r.status.reason}</span>
              </span>
            </li>
          ))}
          {alertas.length === 0 && <li className="text-sm text-gray-600">Nenhum alerta para este veículo{resumo.semBase ? ` · ${resumo.semBase} item(ns) sem base` : ""}.</li>}
        </ul>
        <Link className="btn-secondary min-h-[2.75rem] bg-white" to={`/frota/manutencao/preventiva?plate=${placa}`}>Ver todos os alertas →</Link>
      </section>
    </div>
  );
}
